import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SlotShape } from '../../../../../shapes/Slot';
import FullTimeSlotGrid from './FullTimeSlotGrid';
import { colors } from '../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { isEmptyArray } from '../../../../../core/util/array';
import { getSlotSelectionProperties } from '../util';
import { safeGet } from '../../../../../core/util/object';
import Loading from '../../../../SolvPatternLibrary/Loading';
import {
  useBringSelectedSlotIntoView,
  useInitializeSlotSliceEnd,
  useResetDefaultSlot,
} from './hooks';
import { usePalette } from '~/hooks/usePalette';

export const INITIAL_SLICE_START = 0;
export const INITIAL_SLICE_END = 8;

const ShowMoreOrLess = styled.div.withConfig({ componentId: 'sc-1m7ksrw-0' })<{ color: string }>`
  color: ${(props) => props.color};
  font-size: ${fontSize.small};
  font-family: ${fontFamily.regular};
  letter-spacing: -0.13px;
  line-height: 16px;
  text-align: center;
  margin-top: 12px;
  margin-bottom: 24px;
  cursor: pointer;
  text-decoration: underline;
`;

const SpacedLoading = styled.div.withConfig({ componentId: 'sc-1m7ksrw-1' })`
  text-align: center;
  height: 108px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

type Props = {
  allowExpandInPlace: boolean;
  slots?: SlotShape[];
  timeZone?: string;
  className?: string;
  darkTheme?: boolean;
  handleSelectSlot?: (...args: any[]) => any;
  selectedSlots?: SlotShape[];
  shouldSelectFirstSlotAsInitialValue?: boolean;
  shouldSetSelectedSlotToFirstVisibleSlot: boolean;
  hasSelectedDateFromModal?: boolean;
  location?: any;
};

const emptySlots: SlotShape[] = [];
const ReducedTimeSlotGrid = (props: Props) => {
  const {
    allowExpandInPlace,
    slots = emptySlots,
    timeZone,
    className,
    darkTheme,
    handleSelectSlot,
    selectedSlots,
    shouldSelectFirstSlotAsInitialValue,
    shouldSetSelectedSlotToFirstVisibleSlot,
    hasSelectedDateFromModal,
    location,
  } = props;

  const { t } = useTranslation();

  const { palette } = usePalette();

  const [sliceStart, setSliceStart] = useState(INITIAL_SLICE_START);
  const [sliceEnd, setSliceEnd] = useState(INITIAL_SLICE_END);
  const [hasInitializedSliceStartAndEnd, setHasInitializedSliceStartAndEnd] = useState(false);

  const slicedSlots = slots && slots.slice(sliceStart, sliceEnd);
  const maxSelectedAppointmentDate = useMemo(
    // @ts-expect-error Argument of type 'any[]' is not assignable to ...
    () => Math.max(selectedSlots.map((slot: any) => slot.appointmentDate)),
    [selectedSlots]
  );
  const indexOfSlotCorrespondingToMaxSelectedAppointmentDate = useMemo(
    () =>
      slots && slots.findIndex((slot: any) => slot.appointmentDate === maxSelectedAppointmentDate),
    [maxSelectedAppointmentDate, slots]
  );

  const currentlySelectedAppointmentDate = safeGet(selectedSlots, [])(`${[0]}.appointmentDate`);
  const indexOfSelectedSlot = useMemo(
    () =>
      slots &&
      slots.findIndex((slot: any) => slot.appointmentDate === currentlySelectedAppointmentDate),
    [currentlySelectedAppointmentDate, slots]
  );

  useResetDefaultSlot({ handleSelectSlot, hasSelectedDateFromModal, location, slots });

  useInitializeSlotSliceEnd({
    hasInitializedSliceStartAndEnd,
    selectedSlots,
    setSliceEnd,
    indexOfSlotCorrespondingToMaxSelectedAppointmentDate,
    setHasInitializedSliceStartAndEnd,
  });

  useBringSelectedSlotIntoView({
    slicedSlots,
    currentlySelectedAppointmentDate,
    shouldSetSelectedSlotToFirstVisibleSlot,
    setSliceStart,
    indexOfSelectedSlot,
    setSliceEnd,
  });

  if (isEmptyArray(slots)) {
    return (
      <SpacedLoading>
        <Loading color={colors.crunchBerry} />
      </SpacedLoading>
    );
  }

  const handleClickMore = () => {
    let updatedSliceEnd: any;
    if (sliceEnd === INITIAL_SLICE_END) {
      updatedSliceEnd = 24;
    } else {
      updatedSliceEnd = sliceEnd + 24;
    }

    const handleUpdateGrid = () => {
      const slicedSlots = slots.slice(0, updatedSliceEnd);
      handleSelectSlot?.(selectedSlots, getSlotSelectionProperties(slicedSlots));
    };

    setSliceEnd(updatedSliceEnd);
    handleUpdateGrid();
  };

  const handleClickLess = () => {
    setSliceEnd(INITIAL_SLICE_END);
    const slicedSlots = slots.slice(INITIAL_SLICE_START, INITIAL_SLICE_END);
    handleSelectSlot?.(selectedSlots, getSlotSelectionProperties(slicedSlots));
  };

  const getTimeSlotMenuCta = () => {
    if (slots.length <= INITIAL_SLICE_END) {
      return null;
    }

    if (sliceEnd >= slots.length) {
      return {
        onClick: handleClickLess,
        text: t('bookingTimeSelector.slotSelectorStates.fewerTimes'),
      };
    }

    return {
      onClick: handleClickMore,
      text: t('bookingTimeSelector.slotSelectorStates.moreTimes'),
    };
  };

  const timeSlotMenu = allowExpandInPlace && getTimeSlotMenuCta();

  return (
    <>
      <FullTimeSlotGrid
        className={className}
        darkTheme={darkTheme}
        handleSelectedSlot={handleSelectSlot}
        selectedSlots={selectedSlots}
        shouldSelectFirstSlotAsInitialValue={shouldSelectFirstSlotAsInitialValue}
        slots={slicedSlots}
        timeZone={timeZone}
      />

      <div>
        {timeSlotMenu && (
          <ShowMoreOrLess color={palette.base} onClick={timeSlotMenu.onClick}>
            {timeSlotMenu.text}
          </ShowMoreOrLess>
        )}
      </div>
    </>
  );
};

export default ReducedTimeSlotGrid;
